// frontend/src/components/RecommendationsTab.js

import React, { useState, forwardRef, useImperativeHandle, useEffect, useCallback, useRef } from "react";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { usePrescription } from "./PrescriptionContext";

const FieldLabel = ({ children }) => (
  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
    {children}
  </p>
);

const makeRecId = () => `rec-${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const toLocalRows = (items = []) =>
  items.map((text) => ({ id: makeRecId(), text: text || "" }));

const RecommendationsTab = forwardRef(({
  onDirtyChange,
  onSummaryChange,
  initialData,
}, ref) => {
  const { setRecommendations } = usePrescription();

  const [recommendations, setRecommendationsLocal] = useState([]);
  const [notes, setNotes] = useState("");
  const prevSummaryRef = useRef({ itemCount: 0 });
  const isInitializedRef = useRef(false);
  const isEditingRef = useRef(false);
  const focusIdRef = useRef(null);
  const inputRefs = useRef({});

  // Load initial data once — don't overwrite while user is actively editing
  useEffect(() => {
    if (isEditingRef.current) return;

    if (initialData?.quickRecommendations) {
      setRecommendationsLocal(toLocalRows(initialData.quickRecommendations));
    }
    if (initialData?.notes !== undefined) {
      setNotes(initialData.notes || "");
    }
    isInitializedRef.current = true;
  }, [initialData]);

  const syncContext = useCallback((rows, notesValue) => {
    if (!isInitializedRef.current) return;
    setRecommendations({
      quickRecommendations: rows.map((r) => r.text),
      notes: notesValue,
    });
  }, [setRecommendations]);

  useEffect(() => {
    syncContext(recommendations, notes);
  }, [recommendations, notes, syncContext]);

  useEffect(() => {
    const itemCount = recommendations.filter((r) => r.text && r.text.trim() !== "").length;
    const newSummary = { itemCount };

    if (prevSummaryRef.current.itemCount !== itemCount) {
      prevSummaryRef.current = newSummary;
      onSummaryChange?.(newSummary);
    }
  }, [recommendations, onSummaryChange]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      quickRecommendations: recommendations.map((r) => r.text),
      notes,
    }),
    resetDirty: () => {},
  }));

  const addRecommendation = useCallback(() => {
    const newId = makeRecId();
    focusIdRef.current = newId;
    setRecommendationsLocal((prev) => [...prev, { id: newId, text: "" }]);
    requestAnimationFrame(() => {
      inputRefs.current[newId]?.focus();
    });
  }, []);

  const updateRecommendation = useCallback((id, value) => {
    isEditingRef.current = true;
    setRecommendationsLocal((prev) =>
      prev.map((r) => (r.id === id ? { ...r, text: value } : r))
    );
    onDirtyChange?.(true);
  }, [onDirtyChange]);

  const handleBlur = useCallback(() => {
    // Brief delay so focus can move to another recommendation input
    setTimeout(() => {
      const active = document.activeElement;
      const stillInTab = Object.values(inputRefs.current).some((el) => el === active);
      if (!stillInTab) {
        isEditingRef.current = false;
      }
    }, 100);
  }, []);

  const removeRecommendation = useCallback((id) => {
    setRecommendationsLocal((prev) => prev.filter((r) => r.id !== id));
    onDirtyChange?.(true);
  }, [onDirtyChange]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <FieldLabel>Quick Recommendations</FieldLabel>
        <button
          type="button"
          onClick={addRecommendation}
          className="flex items-center gap-1.5 text-[12px] font-bold text-emerald-600 hover:text-emerald-700"
        >
          <PlusIcon className="w-4 h-4" /> Add Recommendation
        </button>
      </div>

      {recommendations.length === 0 ? (
        <button
          type="button"
          onClick={addRecommendation}
          className="w-full border-2 border-dashed border-slate-200 rounded-xl py-6 text-center hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors"
        >
          <p className="text-[12px] text-slate-400">No recommendations added yet — click to add</p>
        </button>
      ) : (
        <div className="space-y-2">
          {recommendations.map((rec, idx) => (
            <div key={rec.id} className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-slate-400 min-w-[24px]">
                #{idx + 1}
              </span>
              <input
                ref={(el) => {
                  inputRefs.current[rec.id] = el;
                  if (el && focusIdRef.current === rec.id) {
                    el.focus();
                    focusIdRef.current = null;
                  }
                }}
                type="text"
                value={rec.text}
                onChange={(e) => updateRecommendation(rec.id, e.target.value)}
                onFocus={() => { isEditingRef.current = true; }}
                onBlur={handleBlur}
                placeholder="Enter recommendation..."
                className="flex-1 text-[13px] text-slate-700 border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => removeRecommendation(rec.id)}
                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

RecommendationsTab.displayName = "RecommendationsTab";

export default RecommendationsTab;
